import {
  CELL_RANGE_REGEX,
  COLUMN_RANGE_REGEX,
  ROW_RANGE_REGEX,
  cellAdressRegex,
  rowRegex,
  columnRegex,
} from '../../constants/regex';
import { Area, CellPosition, IRange } from '../../types';
import {
  AbsoluteAreaFlags,
  AbsoluteRangeFlags,
} from '../../types/sheet/address';
import { columnLetterToIndex } from '../area/area';

// #region cell range | position
export interface IsCellRange {
  sheetId?: string;
  area: Area;
  absoluteAreaFlags: AbsoluteAreaFlags;
}
export const isCellRange = (address: string): IsCellRange | undefined => {
  const matches = address.match(CELL_RANGE_REGEX);
  // console.log('range address', address);
  if (matches) {
    const [left, right] = address.split(':');
    const start = isCellPosition(left);
    const end = isCellPosition(right);

    if (start && end) {
      const { ci: startCi, ri: startRi } = start.position;
      const { ci: endCi, ri: endRi } = end.position;

      return {
        sheetId: start.sheetId,
        area: {
          start: {
            ci: Math.min(startCi, endCi),
            ri: Math.min(startRi, endRi),
          },
          end: {
            ci: Math.max(startCi, endCi),
            ri: Math.max(startRi, endRi),
          },
        },
        absoluteAreaFlags: {
          start: {
            col: start.absoluteCol,
            row: start.absoluteRow,
          },
          end: {
            col: end.absoluteCol,
            row: end.absoluteRow,
          },
        },
      };
    }
  }
};

export interface IsCellPosition {
  sheetId?: string;
  position: CellPosition;
  absoluteCol: boolean;
  absoluteRow: boolean;
}
export const isCellPosition = (
  cellAddress: string
): IsCellPosition | undefined => {
  // console.log('cellAddress', cellAddress);
  const result = cellAdressRegex.exec(cellAddress);

  if (result) {
    // console.log('result from cell pos', result);
    const sheetId = result[2];
    // console.log('sheetId', sheetId);
    const absoluteCol = result[5];
    const col = result[6];
    const absoluteRow = result[7];
    const row = result[8];

    if (col !== undefined && row !== undefined) {
      const ci = columnLetterToIndex(col);
      const ri = Math.max(+row - 1, 0);

      return {
        sheetId,
        position: {
          ci,
          ri,
        },
        absoluteCol: !!absoluteCol,
        absoluteRow: !!absoluteRow,
      };
    }
  }
};
// #endregion

// #region row address
export interface IsRangeAddress {
  sheetId?: string;
  range: IRange;
  absoluteRangeFlags: AbsoluteRangeFlags;
}
export const isRowRange = (address: string): IsRangeAddress | undefined => {
  const result = address.match(ROW_RANGE_REGEX);

  if (result) {
    const [left, right] = address.split(':');
    const startPos = isRowAddress(left);
    const endPos = isRowAddress(right);

    if (startPos && endPos) {
      return {
        sheetId: startPos.sheetId,
        range: {
          start: Math.min(startPos.index, endPos.index),
          end: Math.max(startPos.index, endPos.index),
        },
        absoluteRangeFlags: {
          start: startPos.absoluteRow,
          end: endPos.absoluteRow,
        },
      };
    }
  }
};

export const isRowAddress = (address: string) => {
  const result = rowRegex.exec(address);

  if (result) {
    const sheetId = result[2];
    const absoluteRow = result[5];
    const rowPos = result[6];

    const index = Math.max(+rowPos - 1, 0);

    return {
      sheetId,
      absoluteRow: !!absoluteRow,
      index,
    };
  }
};
// #endregion

// #region column range
export const isColumnRange = (address: string): IsRangeAddress | undefined => {
  const result = address.match(COLUMN_RANGE_REGEX);

  if (result) {
    const [left, right] = address.split(':');
    const startPos = isColumnAddress(left);
    const endPos = isColumnAddress(right);
    if (startPos && endPos) {
      return {
        sheetId: startPos.sheetId,
        range: {
          start: Math.min(startPos.index, endPos.index),
          end: Math.max(startPos.index, endPos.index),
        },
        absoluteRangeFlags: {
          start: startPos.absoluteCol,
          end: endPos.absoluteCol,
        },
      };
    }
  }
};

export const isColumnAddress = (address: string) => {
  const result = columnRegex.exec(address);
  if (result) {
    const sheetId = result[2];
    const absoluteCol = result[5];
    const colLetter = result[6];

    return {
      sheetId,
      absoluteCol: !!absoluteCol,
      index: columnLetterToIndex(colLetter),
    };
  }
};
// #endregion
